import { Component, type ErrorInfo, type ReactNode } from "react"

import { logger } from "@/lib/logger"

interface AppErrorBoundaryProps {
  children: ReactNode
  redirectTo?: string
}

interface AppErrorBoundaryState {
  hasError: boolean
}

const REDIRECT_LOCK_KEY = "app-error-boundary-redirect-lock-ts"
const REDIRECT_LOCK_TTL_MS = 5000

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error("AppErrorBoundary caught render error", error, {
      componentStack: info.componentStack,
    })

    this.redirectIfSafe()
  }

  private redirectIfSafe() {
    if (typeof window === "undefined") {
      return
    }

    const redirectTo = this.props.redirectTo ?? "/"

    if (window.location.pathname === redirectTo) {
      return
    }

    try {
      const now = Date.now()
      const lockedAt = Number(sessionStorage.getItem(REDIRECT_LOCK_KEY) || "0")
      const hasRecentRedirect =
        Number.isFinite(lockedAt) && now - lockedAt < REDIRECT_LOCK_TTL_MS

      if (hasRecentRedirect) {
        return
      }

      sessionStorage.setItem(REDIRECT_LOCK_KEY, String(now))
    } catch (error) {
      logger.warn("AppErrorBoundary redirect lock failed", error)
    }

    window.location.replace(redirectTo)
  }

  render() {
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}
